import { PointerEvent, useRef } from "react";

// A horizontal swipe is at least this far…
const MIN_DISTANCE_PX = 40;
// …and clearly more sideways than up/down, so a diagonal scroll isn't read as one.
const MIN_RATIO = 1.5;

/**
 * Horizontal swipe on touch and pen, from pointer events (no scroll or touchmove listeners).
 * The element must set `touch-action: pan-y`: vertical drags stay the browser's (the page
 * scrolls), horizontal ones come through as pointer events instead of being cancelled. Mouse
 * drags are ignored — on desktop the dots are the control, and a mouse drag would fight text
 * selection and image dragging.
 *
 * The swipe fires on release, not mid-drag: nothing follows the finger, so there's no
 * per-frame work and no layout read during the gesture.
 */
export const useSwipe = (onSwipe: (dir: "next" | "prev") => void) => {
  const start = useRef<{ x: number; y: number; id: number } | null>(null);
  const swiped = useRef(false);

  return {
    handlers: {
      onPointerDown: (e: PointerEvent) => {
        // A new gesture: a swipe that ended without a click must not swallow the next tap.
        swiped.current = false;
        if (e.pointerType === "mouse" || !e.isPrimary) {
          return;
        }
        start.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
      },
      onPointerUp: (e: PointerEvent) => {
        const s = start.current;
        start.current = null;
        if (!s || s.id !== e.pointerId) {
          return;
        }
        const dx = e.clientX - s.x;
        const dy = e.clientY - s.y;
        if (Math.abs(dx) >= MIN_DISTANCE_PX && Math.abs(dx) > Math.abs(dy) * MIN_RATIO) {
          swiped.current = true;
          // Finger moves left → content advances, as in any carousel.
          onSwipe(dx < 0 ? "next" : "prev");
        }
      },
      onPointerCancel: () => {
        start.current = null;
      },
    },
    /** True (once) if the gesture that just ended was a swipe, so the click it may emit isn't a tap. */
    consumeSwipe: () => {
      const was = swiped.current;
      swiped.current = false;
      return was;
    },
  };
};
